import React, { type ComponentType, useCallback, useMemo } from "react";
import { useFormik } from "formik";
import {
  Button,
  FormHelperText,
  Grid as GridBase,
  InputLabel,
  Typography,
} from "@mui/material";
import * as Yup from "yup";
import { useSnackbar } from "notistack";

import RpTimePicker from "@/components/muiCustom/date/RpTimePicker";
import { EMAIL_CAMPAIGN_CONST } from "@/features/campaigns/email/utils/emailCampaignConstants";
import RpMuiSelect from "@/components/muiCustom/select/RpMuiSelect";
import RpDatePicker from "@/components/muiCustom/date/RpDatePicker";
import { logger } from "@/helpers/loggerHelper";
import { shouldBlockUnsafeSubmission } from "@/helpers/sanitizeHelper";

type LocationId = string | number;

type EmailTemplate = {
  id: LocationId;
  title: string;
  [key: string]: unknown;
};

type BulkSelectionValues = {
  template: string | number;
  schedule_type: string;
  schedule_date: string;
  schedule_time: string;
};

type JoinEmailCampaignBulkSelectionFormProps = {
  templates?: EmailTemplate[];
  onSubmit?: (_values: BulkSelectionValues) => void | Promise<void>;
  isLocationChecked?: number | boolean;
};

type SelectOption = {
  label: string;
  value: string | number;
};

type DateValue = {
  format: (_format: string) => string;
};

const Grid = GridBase as ComponentType<any>;
const DatePicker = RpDatePicker as ComponentType<{
  name?: string;
  placeholder?: string;
  value: string;
  onChange: (_value: DateValue) => void;
  error?: string | false;
  helperText?: string | false;
  onBlur?: () => void;
}>;
const TimePicker = RpTimePicker as ComponentType<{
  name?: string;
  placeholder?: string;
  value: string;
  onChange: (_value: string) => void;
  error?: string | false;
  helperText?: string | false;
  onBlur?: () => void;
}>;

const JoinEmailCampaignBulkSelectionFormValidationSchema = Yup.object({
  template: Yup.string().required("Please select a template"),
  schedule_type: Yup.string().required("Please select schedule"),
  schedule_date: Yup.string().when("schedule_type", {
    is: EMAIL_CAMPAIGN_CONST.SCHEDULED_TYPE.SCHEDULED.value,
    then: (schema) => schema.required("Please select start date"),
    otherwise: (schema) => schema.notRequired(),
  }),
  schedule_time: Yup.string().when("schedule_type", {
    is: EMAIL_CAMPAIGN_CONST.SCHEDULED_TYPE.SCHEDULED.value,
    then: (schema) => schema.required("Please select schedule time"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

function JoinEmailCampaignBulkSelectionForm({
  templates = [],
  onSubmit,
  isLocationChecked,
  //   campaign,
  //   locations,
}: JoinEmailCampaignBulkSelectionFormProps) {
  const { enqueueSnackbar } = useSnackbar();
  const formik = useFormik<BulkSelectionValues>({
    initialValues: {
      template: "",
      schedule_type: "",
      schedule_date: "",
      schedule_time: "",
    },
    validationSchema: JoinEmailCampaignBulkSelectionFormValidationSchema,
    validateOnChange: true,
    validateOnBlur: false,
    validateOnMount: false,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        if (shouldBlockUnsafeSubmission(values)) {
          setSubmitting(false);
          return;
        }

        if (typeof onSubmit === "function") {
          await onSubmit(values);
          // Reset form after successful submission
          resetForm();
        }
      } catch (error) {
        logger.error("Form submission error:", error);
      } finally {
        setSubmitting(false);
      }
    },
    enableReinitialize: true,
  });

  const {
    setFieldValue,
    setFieldTouched,
    touched,
    errors,
    values,
    isSubmitting,
  } = formik;

  const setTouchedWithUpdater = formik.setTouched as unknown as (
    _updater: (_prevValues: Record<string, boolean>) => Record<string, boolean>
  ) => void;

  const handleButtonClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Mark all fields as touched
    await formik.setTouched({
      template: true,
      schedule_type: true,
      schedule_date: true,
      schedule_time: true,
    });

    // Validate and submit
    const errors = await formik.validateForm();
    if (Object.keys(errors).length === 0) {
      if (!isLocationChecked) {
        enqueueSnackbar("Please select a location!", { variant: "error" });
        return;
      } else {
        formik.submitForm();
      }
    }
  };

  const handleTemplateChange = useCallback(
    (v: LocationId[]) => {
      setFieldValue("template", v?.[0] || "");
      setFieldTouched("template", true);
    },
    [setFieldValue, setFieldTouched]
  );

  const handleScheduleTypeChange = useCallback(
    (v: string[]) => {
      const val = v?.[0] || "";
      const isImmediate = val === EMAIL_CAMPAIGN_CONST.SCHEDULED_TYPE.NOW.value;

      if (isImmediate || !val) {
        formik.setValues((prevValues) => ({
          ...prevValues,
          schedule_type: val,
          schedule_date: "",
          schedule_time: "",
        }));

        setTouchedWithUpdater((prevValues) => ({
          ...prevValues,
          schedule_type: true,
          schedule_date: true,
          schedule_time: true,
        }));
      } else {
        setFieldValue("schedule_type", val);
        setFieldTouched("schedule_type", true);
      }
    },
    [formik, setFieldValue, setFieldTouched, setTouchedWithUpdater]
  );

  const templateOptions = useMemo<SelectOption[]>(() => {
    return (
      templates?.map((t) => ({
        label: t.title,
        value: t.id,
      })) || []
    );
  }, [templates]);

  const scheduleOptions = useMemo<SelectOption[]>(() => {
    return [
      {
        label: EMAIL_CAMPAIGN_CONST.SCHEDULED_TYPE.NOW.label,
        value: EMAIL_CAMPAIGN_CONST.SCHEDULED_TYPE.NOW.value,
      },
      {
        label: EMAIL_CAMPAIGN_CONST.SCHEDULED_TYPE.SCHEDULED.label,
        value: EMAIL_CAMPAIGN_CONST.SCHEDULED_TYPE.SCHEDULED.value,
      },
    ];
  }, []);

  const isScheduled =
    values.schedule_type ===
    EMAIL_CAMPAIGN_CONST.SCHEDULED_TYPE.SCHEDULED.value;

  return (
    <div className="w-100 bulk-selection-form">
      <Grid container spacing={2}>
        <Grid
          item
          size={{
            xs: 12,
            md: 6,
            lg: 3,
          }}
        >
          <InputLabel shrink className="m-0">
            Select Template
          </InputLabel>
          <RpMuiSelect
            name="template"
            placeholder="Template"
            options={templateOptions}
            value={[values.template]}
            onChange={handleTemplateChange}
            helperText={errors.template}
            onBlur={() => setFieldTouched("template", true)}
            multiple={false}
          />
          {touched.template && errors.template && (
            <FormHelperText error>{errors.template}</FormHelperText>
          )}
        </Grid>
        <Grid
          item
          size={{
            xs: 12,
            md: 6,
            lg: 3,
          }}
        >
          <InputLabel shrink className="m-0">
            Schedule
          </InputLabel>
          <RpMuiSelect
            name="schedule_type"
            placeholder="Schedule"
            options={scheduleOptions}
            value={[values.schedule_type]}
            onChange={handleScheduleTypeChange}
            helperText={errors.schedule_type}
            onBlur={() => setFieldTouched("schedule_type", true)}
            multiple={false}
          />
          {touched.schedule_type && errors.schedule_type && (
            <FormHelperText error>{errors.schedule_type}</FormHelperText>
          )}
        </Grid>

        {isScheduled && (
          <Grid
            item
            size={{
              xs: 12,
              md: 6,
              lg: 3,
            }}
          >
            <InputLabel shrink className="m-0">
              Start Date
            </InputLabel>
            <DatePicker
              name="schedule_date"
              placeholder="Start Date"
              value={values.schedule_date}
              onChange={(v) => {
                formik.setValues((prevValues) => ({
                  ...prevValues,
                  schedule_date: v.format("YYYY-MM-DD"),
                }));
              }}
              error={touched.schedule_date && errors.schedule_date}
              helperText={touched.schedule_date && errors.schedule_date}
              onBlur={() => setFieldTouched("schedule_date", true)}
            />
          </Grid>
        )}
        {isScheduled && (
          <Grid
            item
            size={{
              xs: 12,
              md: 6,
              lg: 3,
            }}
          >
            <InputLabel shrink className="m-0">
              Set Time
            </InputLabel>
            <TimePicker
              name="schedule_time"
              placeholder="Start Date"
              value={values.schedule_time}
              onChange={(v: string) => {
                formik.setValues((prevValues) => ({
                  ...prevValues,
                  schedule_time: v,
                }));
              }}
              error={touched.schedule_time && errors.schedule_time}
              helperText={touched.schedule_time && errors.schedule_time}
              onBlur={() => setFieldTouched("schedule_time", true)}
            />
          </Grid>
        )}
      </Grid>

      <Grid
        item
        size={{
          xs: 12,
        }}
        className="mt-3"
      >
        <Grid container spacing={2}>
          <Grid
            item
            size={{
              sm: 12,
              md: 8,
            }}
          >
            <Typography variant="caption">
              <span className="fw-b">Pro tip:</span> if you want to make minor
              changes to individual locations, first make bulk selections and
              then edit individual locations.
            </Typography>
          </Grid>
          <Grid
            item
            size={{
              sm: 12,
              md: 4,
            }}
            className="d-flex justify-content-md-center justify-content-lg-end align-items-center"
          >
            <Button
              onClick={handleButtonClick}
              variant="contained"
              color="primary"
              disabled={isSubmitting}
            >
              Apply to Selected Locations Below
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
}

export default JoinEmailCampaignBulkSelectionForm;
